import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import RichTextEditor from '../../../components/RichTextEditor';
import TaskMessages from '../../../components/TaskMessages';
import TaskAccess from '../../../components/TaskAccess';
import TaskFiles from '../../../components/TaskFiles';
import TaskFilesList from '../../../components/TaskFilesList';
import Tabs from '../../../components/tabs';
import ConfirmModal from '../../../components/ConfirmModal';
import UserAvatars from '../../../components/UserAvatars';
import EditTaskModal from './EditTaskModal';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('description'); // 'description', 'comments', 'access'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Состояние для модального окна редактирования
  const [editModal, setEditModal] = useState({ isOpen: false, task: null });

  // Проверяем, является ли текущий пользователь автором задачи
  const isTaskEditor = (task) => {
    if (!task || !task.access_users || !user) return false;
    const accessUser = task.access_users.find(u => u.id === user.id);
    return accessUser && accessUser.type != 'read';
  };

  // Проверяем, является ли текущий пользователь автором задачи
  const isTaskAuthor = (task) => {
    return task && task.uid === user?.id;
  };

  // Определяем табы
  const tabs = React.useMemo(() => [
    {
      id: 'description',
      label: currentLang === 'ru' ? 'Описание задачи' : 'Task Description'
    },
    {
      id: 'comments',
      label: currentLang === 'ru' ? 'Комментарии' : 'Comments'
    },
    {
      id: 'files',
      label: currentLang === 'ru' ? 'Файлы' : 'Files'
    },
    // Таб управления доступом только для автора
    ...(isTaskAuthor(task) ? [{
      id: 'access',
      label: currentLang === 'ru' ? 'Доступ к задаче' : 'Task Access'
    }] : [])
  ], [currentLang, task]);

  useEffect(() => {
    fetchTask();
    fetchSites(); // Загружаем сайты для отображения информации
  }, [id]);

  // Проверяем, что активный таб доступен
  useEffect(() => {
    if (task && tabs.length > 0) {
      const availableTabIds = tabs.map(tab => tab.id);
      if (!availableTabIds.includes(activeTab)) {
        setActiveTab(availableTabIds[0]);
      }
    }
  }, [task, tabs, activeTab]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks/${id}`);

      const data = await response.json();

      if (response.ok && data.success) {
        setTask(data.data);
      } else {
        throw new Error(data.message || 'Ошибка загрузки задачи');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        // Ошибка загрузки сайтов
      }
    } catch (error) {
      // Ошибка загрузки сайтов
    } finally {
      setSitesLoading(false);
    }
  };

  const showDeleteConfirm = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      setError(null);

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo-tasks/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showNotification(currentLang === 'ru' ? 'Задача удалена!' : 'Task deleted!', 'success');
        navigate('/seo/tasks');
      } else {
        throw new Error(data.message || 'Ошибка удаления задачи');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setDeleting(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return currentLang === 'ru' ? 'Ожидает' : 'Pending';
      case 1: return currentLang === 'ru' ? 'В работе' : 'In Progress';
      case 2: return currentLang === 'ru' ? 'Завершена' : 'Completed';
      case 99: return currentLang === 'ru' ? 'Отменена' : 'Cancelled';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      case 99: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Получение информации о сайте
  const getSiteInfo = (siteId) => {
    if (!siteId) return currentLang === 'ru' ? 'Не указан' : 'Not specified';
    
    const site = sites.find(s => s.id === siteId);
    if (site) {
      return `${site.dns} ${site.status === 1 ? '(Активен)' : '(Неактивен)'}`;
    }
    
    return `ID: ${siteId}`;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
        <button
          onClick={() => navigate('/seo/tasks')}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
        >
          {currentLang === 'ru' ? 'Вернуться к списку' : 'Back to List'}
        </button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Задача не найдена' : 'Task not found'}
        </div>
        <button
          onClick={() => navigate('/seo/tasks')}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
        >
          {currentLang === 'ru' ? 'Вернуться к списку' : 'Back to List'}
        </button>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
        <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
          <button
            onClick={() => navigate('/seo/tasks')}
            className="px-2 sm:px-3 py-1 bg-gray-200 text-black rounded-lg hover:bg-gray-600 text-sm sm:text-base"
          >
            ←
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-md sm:text-xl lg:text-2xl font-bold text-gray-900 break-words">
              {task.name}
            </h2>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${getStatusColor(task.status)} w-full sm:w-auto text-center`}>
            {getStatusText(task.status)}
          </span>
          {isTaskEditor(task) && (
            <div className="flex flex-row space-x-2 w-full sm:w-auto">
              <button
                onClick={() => setEditModal({ isOpen: true, task })}
                className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm sm:text-base w-full sm:w-auto"
              >
                {currentLang === 'ru' ? 'Редактировать' : 'Edit'}
              </button>
              <button
                onClick={showDeleteConfirm}
                disabled={deleting}
                className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
              >
                {deleting ? (currentLang === 'ru' ? 'Удаление...' : 'Deleting...') : (currentLang === 'ru' ? 'Удалить' : 'Delete')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Контент */}
      <div className="bg-white rounded-lg shadow-md">
        {/* Табы для просмотра */}
        <div>
          {/* Навигация табов */}
          <div className="p-3 sm:p-4 lg:p-6 pb-0">
          <Tabs 
            tabs={tabs} 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
          </div>
          {/* Контент табов */}
          <div className="p-3 sm:p-4 lg:p-6">
            {activeTab === 'description' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Основная информация в стиле Jira */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 lg:gap-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {currentLang === 'ru' ? 'Основная информация' : 'Basic Information'}
                      </h3>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
                        {/* Дата создания */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0">
                              {currentLang === 'ru' ? 'Создано' : 'Created'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(task.created_at)}</p>
                          </div>
                        </div>

                        {/* Дата обновления */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-0">
                              {currentLang === 'ru' ? 'Обновлено' : 'Updated'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(task.updated_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 lg:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6">
                      

                      {/* Статус */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Статус' : 'Status'}
                            </p>
                            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(task.status)}`}>
                              {getStatusText(task.status)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Прогресс */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Прогресс' : 'Progress'}
                            </p>
                            <div className="flex items-center space-x-2 sm:space-x-3">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${task.pct}%` }}
                                ></div>
                              </div>
                              <span className="text-xs sm:text-sm font-semibold text-gray-900 min-w-[2.5rem] sm:min-w-[3rem]">{task.pct}%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Сайт */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-orange-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Сайт' : 'Site'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{getSiteInfo(task.site_id)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Владелец */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-indigo-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Владелец' : 'Owner'}
                            </p>
                            {/* Аватары пользователей */}
                            {task.access_users && task.access_users.length > 0 && (
                              <div className="mt-2">
                                <UserAvatars 
                                  users={task.access_users} 
                                  maxVisible={5} 
                                  size="sm" 
                                />
                      </div>
                            )}
                    </div>
                  </div>
                </div>

                      {/* Дата начала */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Дата начала' : 'Start Date'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(task.d_start)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Дата окончания */}
                      <div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">
                              {currentLang === 'ru' ? 'Дата окончания' : 'End Date'}
                            </p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(task.d_end)}</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>


                {/* Описание в стиле Jira */}
                {task.text && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {currentLang === 'ru' ? 'Описание' : 'Description'}
                      </h3>
                    </div>
                    <div className="p-3 sm:p-4 lg:p-6">
                          <div 
                            className="prose prose-sm max-w-none text-sm sm:text-base"
                            dangerouslySetInnerHTML={{ __html: task.text }}
                          />
                    </div>
                  </div>
                )}

                {/* Список файлов задачи (только просмотр) */}
                <TaskFilesList taskId={task.id} />

                <TaskMessages taskId={task.id} isTaskEditor={isTaskEditor(task)} />
              </div>
            )}

            {activeTab === 'comments' && (
              <>
                {/* Аватары пользователей с доступом */}
                {task.access_users && task.access_users.length > 0 && (
                  <div className="mb-4 p-3 sm:p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      {currentLang === 'ru' ? 'Пользователи с доступом:' : 'Users with access:'}
                    </h4>
                    <UserAvatars 
                      users={task.access_users} 
                      maxVisible={10} 
                      size="md" 
                    />
                  </div>
                )}
              <TaskMessages taskId={task.id} isTaskEditor={isTaskEditor(task)} />
              </>
            )}

            {activeTab === 'files' && (
              <TaskFiles taskId={task.id} isTaskEditor={isTaskEditor(task)} />
            )}

            {activeTab === 'access' && (
              <TaskAccess task={task} />
            )}
          </div>
        </div>
      </div>

      {/* Модалка редактирования задачи */}
      <EditTaskModal
        isOpen={editModal.isOpen}
        task={editModal.task}
        onClose={() => setEditModal({ isOpen: false, task: null })}
        onUpdate={(updatedTask) => {
          setTask(updatedTask);
          setEditModal({ isOpen: false, task: null });
        }}
      />

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить эту задачу? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this task? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default TaskDetail; 